import React from "react";
import { Link } from "react-router-dom";
import ConciergeForm from "../components/ConciergeForm";
import Seo from "../components/Seo";

const PORTRAIT = "https://images.unsplash.com/photo-1713425884527-66c2e63dfb39?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const RACK = "https://images.pexels.com/photos/13181672/pexels-photo-13181672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const BAG = "https://images.pexels.com/photos/8502482/pexels-photo-8502482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const PILLARS = [
  { eyebrow: "01", title: "100% Authentic", body: "Every bag is verified before shipping. No replicas, no compromises." },
  { eyebrow: "02", title: "Money-back Guarantee", body: "If a bag is ever found to be inauthentic, we refund the full purchase price. No questions asked." },
  { eyebrow: "03", title: "Free Shipping over $200", body: "Tracked end-to-end and delivered worldwide. Free on orders above $200." },
  { eyebrow: "04", title: "14-Day Returns", body: "Changed your mind? Send it back within 14 days in original condition." },
];

const GUARANTEE_STEPS = [
  { eyebrow: "Step 01", title: "Verified before it ships", body: "Every bag goes through our authentication checklist — stitching, hardware, serial codes, materials, dust bag and packaging." },
  { eyebrow: "Step 02", title: "Receive your bag", body: "Tracked delivery to your door. Open, inspect, and try it on with the tags still attached." },
  { eyebrow: "Step 03", title: "Authenticity issue? Full refund", body: "If a third-party authenticator ever determines a bag is not genuine, we refund 100% of what you paid — including shipping. No questions, no fine print." },
];

export default function About() {
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      <Seo
        title="About & Style Advice"
        description="Elegant Affairs is a hand-curated edit of authenticated designer luxury bags — direct to your door. Book a private style advice session with our concierge."
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-end mb-24">
          <div className="col-span-12 md:col-span-7 fade-up">
            <p className="eyebrow">About</p>
            <h1 className="font-display text-5xl md:text-[8vw] mt-4 leading-[0.92] tracking-tighter">
              Designer bags. <em className="font-medium italic">Direct to your door.</em>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5 fade-up-2">
            <p className="text-[var(--mn-ink-2)] leading-relaxed text-lg">
              Elegant Affairs is a curated online store for designer luxury bags.
              We hand-pick pieces from established and emerging labels and ship them
              worldwide — no boutique queues, no hassle.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-32">
          <div className="col-span-12 md:col-span-7">
            <div className="aspect-[5/6] overflow-hidden">
              <img src={PORTRAIT} alt="Editorial" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-32">
            <p className="eyebrow">Why Elegant Affairs</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tighter">
              The bag, <em className="font-medium italic">without the noise.</em>
            </h2>
            <p className="mt-6 text-[var(--mn-ink-2)] leading-relaxed">
              We do the editing so you don't have to. A small, considered selection
              of designer bags — refreshed regularly, shipped quickly, returned painlessly.
            </p>
            <ul className="mt-10 space-y-5">
              {PILLARS.map((p) => (
                <li key={p.eyebrow} className="flex gap-5 border-t border-[var(--mn-border)] pt-5">
                  <span className="font-mono-luxe text-xs text-[var(--mn-muted)] pt-1 shrink-0 w-8">{p.eyebrow}</span>
                  <div>
                    <p className="font-display text-xl tracking-tight">{p.title}</p>
                    <p className="text-sm text-[var(--mn-ink-2)] mt-1 leading-relaxed">{p.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {/* Money-back authenticity guarantee explainer */}
        <section className="bg-[var(--mn-ink)] text-[var(--mn-bg)] -mx-6 md:-mx-12 px-6 md:px-12 py-20 md:py-28 mb-24" data-testid="guarantee-section">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-end mb-12">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow text-white/60">Our Promise</p>
              <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tighter">
                Money-back <em className="font-light italic">authenticity guarantee.</em>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-white/70 leading-relaxed">
                Authenticity is not a bonus feature — it's the entire promise. Every bag we
                ship is inspected and verified. If anything we send you is ever found to be
                inauthentic, we make it right immediately.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 border-t border-white/10 pt-12">
            {GUARANTEE_STEPS.map((s) => (
              <div key={s.eyebrow} data-testid={`guarantee-${s.eyebrow.replace(/\s+/g, '-').toLowerCase()}`}>
                <p className="font-mono-luxe text-xs text-white/50 uppercase tracking-[0.22em]">{s.eyebrow}</p>
                <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight tracking-tight">{s.title}</h3>
                <p className="text-sm text-white/70 mt-3 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-[11px] uppercase tracking-[0.22em] text-white/40">
            Refund issued within 5 business days of confirmed inauthenticity.
          </p>
        </section>



        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-24">
          <div className="col-span-12 md:col-span-5 md:pt-24">
            <p className="eyebrow">Style Advice</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tighter">
              Need help choosing? <em className="font-medium italic">Just ask.</em>
            </h2>
            <p className="mt-6 text-[var(--mn-ink-2)] leading-relaxed">
              Tell us what you're looking for — colour, occasion, gift, or a specific
              designer — and a member of our team will reply within 24 hours with curated
              suggestions from the collection.
            </p>
            <Link to="/shop" className="btn-primary mt-8" data-testid="about-shop-cta">
              Browse the Edit
            </Link>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="aspect-[3/4] overflow-hidden">
              <img src={RACK} alt="Curated rack" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-6 md:col-span-4">
            <div className="aspect-[4/5] overflow-hidden md:mt-16">
              <img src={BAG} alt="Designer bag" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>

      <ConciergeForm />
    </div>
  );
}
